<script>
    import { createEventDispatcher } from "svelte";
    import { fade, scale } from "svelte/transition";
    import toast from "svelte-french-toast";
    import axios from "../../utiles/axios.util";

    export let isOpen = false;
    const dispatch = createEventDispatcher();

    // 업로드 엑셀 필수 헤더 (백엔드 BsnMngmtSelngService.MNGMT_HEADERS 와 동일)
    const REQUIRED_HEADERS = [
        { name: "품번", desc: "품목 마스터(BSS_ITEM)에 있는 품번", example: "900000DY00" },
        { name: "일자", desc: "날짜 (엑셀 날짜 또는 2026-10-05 형식)", example: "2026-10-05" },
        { name: "수량", desc: "숫자 (0 가능)", example: "10" },
        { name: "금액", desc: "숫자 (0 가능)", example: "480000000" },
    ];

    let file = null;
    let fileInput;
    let isUploading = false;
    let isDownloading = false;
    let errors = [];
    let resultMsg = "";

    $: if (isOpen) {
        resetState();
    }

    function resetState() {
        file = null;
        errors = [];
        resultMsg = "";
        if (fileInput) fileInput.value = "";
    }

    function handleFileChange(e) {
        file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
        errors = [];
        resultMsg = "";
    }

    async function handleDownloadTemplate() {
        isDownloading = true;
        try {
            const res = await axios.get("bsnMngmtSelng/mngmtTemplate", { responseType: "blob" });
            const blob = new Blob([res.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "경영계획_업로드_양식.xlsx";
            link.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            toast.error("양식 다운로드에 실패했습니다.");
        } finally {
            isDownloading = false;
        }
    }

    async function handleUpload() {
        if (!file) {
            toast.error("업로드할 엑셀 파일을 선택해 주세요.");
            return;
        }
        isUploading = true;
        errors = [];
        resultMsg = "";
        try {
            // 1) 저장 없이 파일 검증 → 오류가 있으면 확인창 없이 바로 오류 표시, 없으면 건수 확인
            const checkData = new FormData();
            checkData.append("file", file);
            checkData.append("dryRun", "true");            const check = await axios.post("bsnMngmtSelng/mngmtUpload", checkData, { timeout: 120000 });
            const cb = check && check.data ? check.data : {};
            if (cb.code === 400) {
                errors = (cb.data && cb.data.errors) || [cb.msg || "검증 오류"];
                return;
            }
            if (cb.code !== 200) {
                errors = [cb.msg || "파일 검증에 실패했습니다."];
                return;
            }
            const c = cb.data || {};
            if (!window.confirm(`정말 업로드 하시겠습니까?\n\n총 ${c.inserted}건 (품번 ${c.items}개, ${(c.years || []).join(", ")}년)`)) {
                return;
            }

            // 2) 실제 저장
            const formData = new FormData();
            formData.append("file", file);            const res = await axios.post("bsnMngmtSelng/mngmtUpload", formData, { timeout: 120000 });
            const body = res && res.data ? res.data : {};

            if (body.code === 200) {
                const d = body.data || {};
                resultMsg = `${d.inserted}건 등록 (품번 ${d.items}개, ${(d.years || []).join(", ")}년` +
                    (d.replaced > 0 ? `, 기존 ${d.replaced}건 덮어씀)` : ")");
                toast.success("경영계획이 업로드되었습니다.", { duration: 2000 });
                dispatch("uploaded");
            } else if (body.code === 400) {
                errors = (body.data && body.data.errors) || [body.msg || "검증 오류"];
            } else {
                errors = [body.msg || "업로드에 실패했습니다."];
            }
        } catch (e) {
            console.error(e);
            const msg = e && e.response && e.response.data && e.response.data.msg;
            errors = [msg || "업로드 중 오류가 발생했습니다."];
        } finally {
            isUploading = false;
        }
    }

    function handleClose() {
        dispatch("close");
    }
</script>

{#if isOpen}
    <div class="modal-backdrop" on:click={handleClose} transition:fade={{ duration: 150 }}>
        <div class="modal-box" on:click|stopPropagation transition:scale={{ duration: 180, start: 0.95 }}>
            <div class="modal-header">
                <span class="modal-title">경영계획 엑셀 업로드</span>
                <button class="close-btn" on:click={handleClose}>✕</button>
            </div>

            <div class="modal-body">
                <!-- 1단계: 양식 다운로드 (처음 사용할 때 가장 먼저 보이도록 맨 위에 배치) -->
                <div class="step-row step-download">
                    <span class="step-no">1</span>
                    <span class="step-label">양식을 내려받아 작성</span>
                    <button type="button" class="btn btn-primary download-btn" on:click={handleDownloadTemplate} disabled={isDownloading}>
                        {isDownloading ? "다운로드 중..." : "⬇ 양식 다운로드"}
                    </button>
                </div>

                <div class="section-title">필수 헤더 (1행)</div>
                <table class="header-table">
                    <thead>
                        <tr>
                            <th>헤더</th>
                            <th>입력 내용</th>
                            <th>예시</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each REQUIRED_HEADERS as h}
                            <tr>
                                <td class="h-name">{h.name}</td>
                                <td>{h.desc}</td>
                                <td class="h-example">{h.example}</td>
                            </tr>
                        {/each}
                    </tbody>
                </table>

                <ul class="notes">
                    <li><strong>경영 업로드는 판매 기준으로만 업로드됩니다.</strong> (실적유형이 출하일 때도 동일)</li>
                    <li>한 행 = 품번 1개 × 일자 1일 입니다. (2행부터 입력)</li>
                    <li>품명, 품목그룹, 제품유형, 기종은 품번으로 자동 채워집니다.</li>
                    <li>같은 품번의 같은 월을 다시 올리면 그 월의 기존 경영 데이터는 모두 새 값으로 교체됩니다.</li>
                    <li>오류가 한 행이라도 있으면 아무것도 저장되지 않습니다.</li>
                </ul>

                <div class="step-row">
                    <span class="step-no">2</span>
                    <span class="step-label">작성한 파일 선택</span>
                    <input
                        bind:this={fileInput}
                        type="file"
                        accept=".xlsx,.xls"
                        class="file-input"
                        on:change={handleFileChange}
                    />
                </div>

                {#if resultMsg}
                    <div class="result-box success">✔ {resultMsg}</div>
                {/if}
                {#if errors.length > 0}
                    <div class="result-box error">
                        <div class="error-title">저장되지 않았습니다. 아래 내용을 수정한 뒤 다시 업로드해 주세요.</div>
                        <ul>
                            {#each errors as err}
                                <li>{err}</li>
                            {/each}
                        </ul>
                    </div>
                {/if}
            </div>

            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" on:click={handleClose}>닫기</button>
                <button type="button" class="btn btn-primary" on:click={handleUpload} disabled={isUploading || !file}>
                    {isUploading ? "업로드 중..." : "3. 업로드"}
                </button>
            </div>
        </div>
    </div>
{/if}

<style>
    .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.6);
        backdrop-filter: blur(2px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
        padding: 16px;
    }
    .modal-box {
        background: #ffffff;
        width: 560px;
        max-width: 95vw;
        max-height: 92vh;
        border-radius: 8px;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border: 1px solid #cbd5e1;
    }
    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background: #1e293b;
        color: white;
    }
    .modal-title {
        font-size: 0.95rem;
        font-weight: 800;
    }
    .close-btn {
        background: transparent;
        border: none;
        color: #94a3b8;
        font-size: 1.1rem;
        cursor: pointer;
    }
    .close-btn:hover {
        color: white;
    }
    .modal-body {
        padding: 14px 18px;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
    .section-title {
        font-size: 0.78rem;
        font-weight: 800;
        color: #334155;
    }
    .header-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.8rem;
    }
    .header-table th,
    .header-table td {
        border: 1px solid #e2e8f0;
        padding: 5px 8px;
        text-align: left;
    }
    .header-table th {
        background: #f1f5f9;
        color: #475569;
        font-weight: 700;
    }
    .h-name {
        font-weight: 800;
        color: #1d4ed8;
        white-space: nowrap;
    }
    .h-example {
        font-family: monospace;
        color: #64748b;
    }
    .notes {
        margin: 0;
        padding-left: 18px;
        font-size: 0.75rem;
        color: #64748b;
        line-height: 1.6;
    }
    .step-row {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .step-download {
        padding: 10px 12px;
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        border-radius: 6px;
    }
    .step-no {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #2563eb;
        color: #ffffff;
        font-size: 0.75rem;
        font-weight: 800;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
    .step-label {
        font-size: 0.82rem;
        font-weight: 800;
        color: #1e293b;
        white-space: nowrap;
    }
    .download-btn {
        margin-left: auto;
    }
    .file-input {
        flex: 1;
        min-width: 0;
        font-size: 0.8rem;
    }
    .result-box {
        font-size: 0.8rem;
        padding: 8px 10px;
        border-radius: 4px;
        border: 1px solid;
    }
    .result-box.success {
        background: #f0fdf4;
        border-color: #bbf7d0;
        color: #166534;
        font-weight: 700;
    }
    .result-box.error {
        background: #fef2f2;
        border-color: #fecaca;
        color: #b91c1c;
        max-height: 180px;
        overflow-y: auto;
    }
    .error-title {
        font-weight: 800;
        margin-bottom: 4px;
    }
    .result-box ul {
        margin: 0;
        padding-left: 18px;
        line-height: 1.55;
    }
    .modal-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
        padding: 10px 16px;
        background: #f8fafc;
        border-top: 1px solid #e2e8f0;
    }
    .btn {
        padding: 6px 16px;
        border-radius: 4px;
        font-size: 0.8rem;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s;
    }
    .btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    .btn-secondary {
        background: #ffffff;
        border: 1px solid #cbd5e1;
        color: #475569;
    }
    .btn-secondary:hover:not(:disabled) {
        background: #f1f5f9;
    }
    .btn-primary {
        background: #2563eb;
        border: 1px solid #1d4ed8;
        color: white;
    }
    .btn-primary:hover:not(:disabled) {
        background: #1d4ed8;
    }
</style>
